import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Space, Button, message, Tag, Tooltip } from 'antd';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { rideAPI, historyAPI } from '../services/api';
import { AppSettings, RideRankingItem, SelectOption, HistoryGranularity } from '../types';
import { compareSnapshotsDesc } from '../utils/snapshot';
import HistoryControls from './HistoryControls';

interface RideRankingsProps {
  settings: AppSettings;
}

const RideRankings: React.FC<RideRankingsProps> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RideRankingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rideId, setRideId] = useState<number | null>(null);
  const [rideTypes, setRideTypes] = useState<SelectOption[]>([]);
  // 历史对比相关
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [granularity, setGranularity] = useState<HistoryGranularity>('day');
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [snapshotFrom, setSnapshotFrom] = useState<string | undefined>(undefined);
  const [snapshotTo, setSnapshotTo] = useState<string | undefined>(undefined);

  // 加载坐骑类型
  useEffect(() => {
    const loadRideTypes = async () => {
      try {
        const result = await rideAPI.getTypes();
        setRideTypes(result);
      } catch (error) {
        console.error('加载坐骑类型失败:', error);
      }
    };
    loadRideTypes();
  }, []);

  // 加载数据
  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      if (historyEnabled && snapshotFrom && snapshotTo) {
        const res = await historyAPI.compare<RideRankingItem>({
          category: 'ride',
          granularity,
          snapshot_from: snapshotFrom,
          snapshot_to: snapshotTo,
          server_id: settings.serverId ?? undefined,
          ride_id: rideId ?? undefined,
          page,
          page_size: settings.pageSize,
        });
        const items = (res.diffs || []).map((d: any, index: number) => {
          const toItem = d.to || {};
          const fromItem = d.from || {};
          const newRank = (d.new_rank ?? toItem.rank ?? 0) as number;
          const oldRank = (d.old_rank ?? fromItem.rank ?? 0) as number;
          const newScore = (d.new_score ?? toItem.ride_score ?? toItem.score ?? 0) as number;
          const oldScore = (d.old_score ?? fromItem.ride_score ?? fromItem.score ?? 0) as number;
          const scoreDelta = newScore - oldScore;
          const rankDelta = newRank - oldRank;
          return {
            ...toItem,
            rank: newRank,
            formatted_score: toItem.formatted_score ?? (toItem.ride_score != null ? String(toItem.ride_score) : (toItem.score != null ? String(toItem.score) : '')),
            filtered_rank: (page - 1) * settings.pageSize + index + 1,
            score_delta: scoreDelta,
            rank_delta: rankDelta,
            is_new: !!d.is_new,
          } as RideRankingItem & { score_delta: number; rank_delta: number; is_new?: boolean };
        });
        setData(items);
        setTotal(res.total ?? items.length);
        setCurrentPage(res.page ?? page);
      } else if (snapshotTo) {
        const result = await historyAPI.getHistory<RideRankingItem>({
          category: 'ride',
          granularity,
          snapshot: snapshotTo,
          server_id: settings.serverId ?? undefined,
          ride_id: rideId ?? undefined,
          page,
          page_size: settings.pageSize,
        });
        let items = (result.items || []).map((item: any) => {
          const rank = (item.rank ?? item.global_rank ?? item.server_rank ?? 0) as number;
          const formatted_score = item.formatted_score ?? (item.ride_score != null ? String(item.ride_score) : (item.score != null ? String(item.score) : ''));
          return { ...item, rank, formatted_score } as RideRankingItem;
        });
        // 历史模式服务端已筛选，无需前端再次过滤
        items.sort((a: any, b: any) => (a.rank && b.rank) ? a.rank - b.rank : (b.ride_score ?? b.score ?? 0) - (a.ride_score ?? a.score ?? 0));
        const itemsWithFilteredRank = items.map((item, index) => ({
          ...item,
          filtered_rank: (page - 1) * settings.pageSize + index + 1,
        }));
        setData(itemsWithFilteredRank);
        setTotal(result.total ?? itemsWithFilteredRank.length);
        setCurrentPage(result.page ?? page);
      } else {
        const result = await rideAPI.getRankings({
          page,
          page_size: settings.pageSize,
          server_id: settings.serverId,
          ride_id: rideId,
          use_units: settings.useUnits,
        });

        const itemsWithFilteredRank = result.items.map((item, index) => ({
          ...item,
          filtered_rank: (page - 1) * settings.pageSize + index + 1,
        }));

        setData(itemsWithFilteredRank);
        setTotal(result.total);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, [settings.pageSize, settings.serverId, settings.useUnits, rideId, historyEnabled, granularity, snapshotFrom, snapshotTo]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const resp = await historyAPI.listSnapshots({ category: 'ride', granularity, limit: 60 });
  const snaps = (resp.snapshots || []).slice().sort(compareSnapshotsDesc);
        setSnapshots(snaps);
        if (historyEnabled) {
          if (snaps.length >= 2) {
            setSnapshotFrom(snaps[1]);
            setSnapshotTo(snaps[0]);
          }
        } else {
          if (snaps.length >= 1) {
            setSnapshotTo(snaps[0]);
          }
        }
      } catch {}
    };
    fetchSnapshots();
  }, [historyEnabled, granularity, settings.serverId]);

  const handleRefresh = () => {
    loadData(currentPage);
  };

  const handleRideChange = (value: number | null) => {
    setRideId(value);
    setCurrentPage(1);
  };

  const columns = [
    {
      title: '总榜排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <Tag color={rank <= 3 ? 'gold' : rank <= 10 ? 'orange' : 'default'}>
          {rank}
        </Tag>
      ),
    },
    {
      title: '筛选排名',
      dataIndex: 'filtered_rank',
      key: 'filtered_rank',
      width: 80,
      render: (rank: number) => (
        <Tag color="blue">{rank}</Tag>
      ),
    },
    {
      title: '坐骑种类',
      dataIndex: 'ride_name',
      key: 'ride_name',
      width: 120,
      render: (name: string) => (
        <span style={{ fontWeight: 'bold', color: '#13c2c2' }}>{name}</span>
      ),
    },
    {
      title: '战力',
      dataIndex: 'formatted_score',
      key: 'score',
      width: 100,
      render: (score: string) => (
        <span style={{ fontWeight: 'bold', color: '#f5222d' }}>{score}</span>
      ),
    },
    {
      title: '等级',
      dataIndex: 'ride_level',
      key: 'ride_level',
      width: 70,
      render: (lv: number) => (
        <Tag color="green">{lv}</Tag>
      ),
    },
    {
      title: '坐骑昵称',
      dataIndex: 'ride_nickname',
      key: 'ride_nickname',
      width: 120,
      render: (nickname: string) => (
        <Tooltip title={nickname}>
          <span style={{ color: '#fa8c16' }}>{nickname || '无昵称'}</span>
        </Tooltip>
      ),
    },
    {
      title: '主人名字',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 120,
      render: (name: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{name}</span>
      ),
    },
    {
      title: '坐骑皮肤',
      dataIndex: 'ride_skin_name',
      key: 'ride_skin_name',
      width: 120,
      render: (skin: string) => skin || '默认',
    },
    {
      title: '主人UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 120,
      render: (uid: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{uid}</span>
      ),
    },
    {
      title: '服务器',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 80,
      render: (serverId: number) => (
        <Tag color="purple">服务器{serverId}</Tag>
      ),
    },
    // 对比模式新增列：分数/排名变化
    ...(historyEnabled ? [
      {
        title: '战力变化',
        dataIndex: 'score_delta',
        key: 'score_delta',
        width: 110,
        align: 'center' as const,
        render: (delta: number) => (
          <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) > 0 ? '#52c41a' : '#f5222d', fontWeight: 500 }}>
            {delta > 0 ? `+${delta}` : `${delta || 0}`}
          </span>
        ),
      },
      {
        title: '排名变化',
        dataIndex: 'rank_delta',
        key: 'rank_delta',
        width: 120,
        align: 'center' as const,
        render: (delta: number, record: any) => {
          if (record?.is_new) {
            return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
          }
          return (
            <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) < 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}>
              {delta < 0 ? `⬆ ${Math.abs(delta)}` : delta > 0 ? `⬇︎ ${delta}` : '—'}
            </span>
          );
        },
      },
    ] : []),
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <span>坐骑排行榜</span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
              size="small"
            >
              刷新
            </Button>
          </Space>
        }
        extra={
          <Space>
            <HistoryControls
              enabled={historyEnabled}
              granularity={granularity}
              snapshots={snapshots}
              from={snapshotFrom}
              to={snapshotTo}
              onChange={(v) => {
                setHistoryEnabled(v.enabled);
                setGranularity(v.granularity);
                setSnapshotFrom(v.from);
                setSnapshotTo(v.to);
              }}
            />
            <FilterOutlined />
            <Select
              placeholder="选择坐骑类型"
              style={{ width: 140 }}
              value={rideId}
              onChange={handleRideChange}
              allowClear
            >
              {rideTypes.map(ride => (
                <Select.Option key={ride.id} value={ride.id}>
                  {ride.name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        <Table
          className="ranking-table"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="uid"
          scroll={{ x: 1000 }}
          pagination={{
            current: currentPage,
            pageSize: settings.pageSize,
            total: total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page) => loadData(page),
          }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default RideRankings;
